/*
 * Copyright (c) 2024 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { Component, ChangeDetectionStrategy } from "@angular/core";
import { Inject } from "@angular/core";
import { Injectable } from "@angular/core";
import { MatDialogTitle } from "@angular/material/dialog";
import { MatDialogContent } from "@angular/material/dialog";
import { MatDialogActions } from "@angular/material/dialog";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatDialogRef } from "@angular/material/dialog";
import { JsonPipe } from "@angular/common";
import { AsyncPipe } from "@angular/common";

import { MatInputModule } from "@angular/material/input";
import { FormsModule } from "@angular/forms";
import { ReactiveFormsModule } from "@angular/forms";
import { FormControl } from "@angular/forms";
import { Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { AnvilorCommonModule } from "@anvilor/ngx-apps-core";
import { MatActionButtonConfig } from "@anvilor/ngx-apps-core";
import { ApiCallStatus } from "@anvilor/ngx-apps-core";
import { AnvilorNotificationsService } from "@anvilor/ngx-apps-core";
import { ObjectId } from "@anvilor/ngx-apps-core";
import { AnvilorFormsApiDavService } from "./dav-authentication";
import { LeaseInfo } from "./dav-authentication";
import { ComponentStore } from "@ngrx/component-store";
import { Observable } from "rxjs";
import { withLatestFrom } from "rxjs";
import { map } from "rxjs/operators";
import { tap } from "rxjs/operators";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { SubscriptionGroup } from "@anvilor/ngx-core-utils";
import { filterNullish } from "@anvilor/ngx-core-utils";
import { ResponseData } from "../../../models/submission-edit";
import { DialogActionButton } from "@anvilor/ngx-apps-core";

export interface DavCredentialsDialogData {
  dav_key: string;
  dav_user: string;
  response_oid: string | ObjectId;
}

export interface DavAuthenticationState {
  make_lease_status: ApiCallStatus;
  post_get_response_data_status: ApiCallStatus;
  lease_info?: LeaseInfo;
}

@Injectable()
export class DavAuthenticationStore extends ComponentStore<DavAuthenticationState> {
  constructor() {
    super({ make_lease_status: "active", post_get_response_data_status: "none" });
  }
}

@Component({
  selector: "app-dav-credentials-dialog",
  styles: [
    `
      .auth-button-wrap {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: center;
        padding: 12px;
      }

      .spinner-wrap {
        width: 100%;
        display: flex;
        justify-content: center;
      }
    `,
  ],
  template: `
    <h2 mat-dialog-title>Authentication Required</h2>
    <mat-dialog-content>
      @if (leaseInfo$ | async; as leaseInfo) {
      <form>
        <div>
          <input style="display: none" type="text" autocomplete="username" />
          @if (leaseInfo.is_password) {
          <mat-form-field appearance="fill">
            <mat-label>Password</mat-label>
            <input
              matInput
              [formControl]="passwordFc"
              type="password"
              autocomplete="current-password"
            />
          </mat-form-field>
          }
        </div>
        <div>
          @if (leaseInfo.is_sms) {
          <mat-form-field appearance="fill">
            <mat-label>SMS Code</mat-label>
            <input
              matInput
              [formControl]="smsCodeFc"
              inputmode="numeric"
              autocomplete="one-time-code"
            />
          </mat-form-field>
          }
        </div>
      </form>
      } @else {
      <div class="spinner-wrap">
        <mat-spinner mode="indeterminate"></mat-spinner>
      </div>
      }
    </mat-dialog-content>
    @if (leaseInfo$ | async) {
    <div class="auth-button-wrap">
      @if (postActionState$ | async; as actionState) {
      <nvl-mat-action-button
        [config]="authButtonConfig"
        [disabled]="!(smsCodeFc.valid && passwordFc.valid)"
        [actionState]="actionState"
        class="pd-sm"
      >
        <mat-icon>lock</mat-icon>&nbsp;Authenticate
      </nvl-mat-action-button>
      }
    </div>
    }
  `,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    AnvilorCommonModule,
    AsyncPipe,
    MatProgressSpinnerModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DavAuthenticationStore],
})
export class DavCredentialsDialogComponent {
  passwordFc = new FormControl("");
  smsCodeFc = new FormControl("");

  postActionState$: Observable<"none" | "active" | undefined>;

  authButtonConfig: MatActionButtonConfig & DialogActionButton = {
    color: "accent",
    click: () => this.onClickAuthenticate(),
    keepDialogOpen: true,
  };

  leaseInfo$: Observable<LeaseInfo | undefined>;

  private accessCode: string | null = null;
  private _subscriptions: SubscriptionGroup = new SubscriptionGroup();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DavCredentialsDialogData,
    private davApi: AnvilorFormsApiDavService,
    private ref: MatDialogRef<
      DavCredentialsDialogComponent,
      { lease: LeaseInfo; response_data: ResponseData }
    >,
    private store: DavAuthenticationStore,
    private notify: AnvilorNotificationsService
  ) {
    this.postActionState$ = this.store
      .select((s) => s.post_get_response_data_status)
      .pipe(map((state) => (state === "active" ? "active" : "none")));

    this.leaseInfo$ = this.store.select((state) => state.lease_info);

    this._subscriptions.push(
      this.davApi
        .postMakeLease(this.data.dav_key, this.data.dav_user)
        .pipe(
          tap((lease_info) => {
            this.store.patchState({ lease_info, make_lease_status: "success" });
            this.accessCode = lease_info.access_code;
            if (lease_info.is_password) {
              this.passwordFc.addValidators(Validators.required);
            }
            if (lease_info.is_sms) {
              this.smsCodeFc.addValidators(Validators.required);
            }
          })
        )
        .subscribe()
    );
  }

  onClickAuthenticate(): any {
    this.store.patchState({ post_get_response_data_status: "active" });
    this.davApi
      .postGetResponseData({
        response_oid: this.data.response_oid,
        access_code: this.accessCode!,
        document_password: this.passwordFc.value ?? undefined,
        sms_code: this.smsCodeFc.value ?? undefined,
      })
      .pipe(
        withLatestFrom(
          this.store.select((state) => state.lease_info).pipe(filterNullish())
        ),
        tap({
          next: ([response_data, lease]) => {
            this.store.patchState({ post_get_response_data_status: "success" });
            this.ref.close({ lease, response_data });
          },
          error: (error) => {
            this.store.patchState({ post_get_response_data_status: "error" });
            this.notify.error.passive({
              error_code: "DAV_AUTHENTICATION_FAILED",
              body: "Invalid credentials were entered",
              server_error_info: error,
              title: "Failure",
            });
          },
        })
      )
      .subscribe();
  }
}
